import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Card, ConfirmDialog, Flex, Tooltip } from 'core/components';
import { Field, FormDialog, InputGroup, InputMultiple, PasswordInput, Select, SingleLineErrors } from 'core/form';
import { addMetadataToOptions, buildOptionsFromMap } from 'core/form/components/modalSelect/selectHelpers';
import { CREDENTIAL_TYPE_LABELS, CREDENTIAL_TYPE_TEMPLATES } from '@kentik/ui-shared/credentials/constants';
import LabelSelector from 'app/components/labels/LabelSelector';
import { isDestructiveCredentialTypeChange } from '@kentik/ui-shared/credentials/util';
import CredentialTypeOptionRenderer from 'app/views/settings/credentials/components/CredentialTypeOptionRenderer';
import multiKeyNameConflict from 'app/forms/config/validators/multiKeyNameConflict';

export const fields = {
  name: {
    label: 'Name',
    rules: 'required|alpha_dash',
    transform: {
      out: (value) => value.trim()
    },
    messages: {
      not_in: 'Name must be unique'
    }
  },
  description: {
    label: 'Description'
  },
  type: {
    label: 'Type',
    rules: 'required',
    options: addMetadataToOptions(buildOptionsFromMap(CREDENTIAL_TYPE_LABELS), CREDENTIAL_TYPE_TEMPLATES)
  },
  tokens: {
    isComplexArray: true
  },
  'tokens[].key': {
    label: 'Key',
    rules: `required|alpha_dash|${multiKeyNameConflict}`
  },
  'tokens[].value': {
    label: 'Value'
  },
  labels: {
    label: 'Labels',
    defaultValue: []
  }
};

const Confirm = ({ deviceCount = 0, ...props }) => (
  <ConfirmDialog
    {...props}
    title="Credential has associated devices"
    confirmButtonText="Confirm"
    text={`Are you sure you want to change the type of this credential? It is being used by ${deviceCount} device${
      deviceCount > 1 ? 's' : ''
    }.`}
  />
);

@inject('$labels', '$auth')
@observer
export default class CredentialDialog extends Component {
  state = {
    isCredentialFromTemplate: false
  };

  form;

  componentDidMount() {
    const { model } = this.props;
    this.applyTemplate(model.get('type'));
  }

  // When modifying the form, all the old members of the form are no longer considered "pristine" and so will trigger validation
  enableErrors = (field) =>
    field.fieldStates.forEach(({ key, value }) => {
      key.setPristine(false);
      value.setPristine(false);
    });

  handleAddToken = (field) => {
    const { model } = this.props;
    this.enableErrors(field);
    field.add(model.initialTokenValue);
  };

  handleRemoveToken = (field, index) => {
    this.enableErrors(field);
    field.remove(index);
  };

  /**
   * Deleting then reconstructing consumer references used by Synthetics is currently too difficult and ambiguous to attempt.
   * @returns boolean
   */
  get isConsumedBySynthetics() {
    const { model } = this.props;
    return !!model.synthConsumers.length;
  }

  finalizeSave = async (form, values) => {
    const { $labels, model, onClose } = this.props;
    this.setState({ isLoading: true, confirmDialogOpen: false });

    try {
      await model.save(values);
      this.setState({ isLoading: false });
      await Promise.all([model.collection.fetch({ force: true }), $labels.labels.fetch({ force: true })]);
      onClose(values);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleSave = async (form, values) => {
    const { model } = this.props;
    const { isChangingTemplate } = this.state;

    if (isChangingTemplate && model.deviceConsumers.length) {
      this.setState({ confirmDialogOpen: true, storedValues: values });
      // Saving is not completed at this stage
      return Promise.reject();
    }
    return this.finalizeSave(form, values);
  };

  handleTypeChange = (field, value) => {
    const { form } = field;
    const { model } = this.props;
    const type = value;

    // are we *changing* to a templated type?
    const isChangingTemplate = isDestructiveCredentialTypeChange(type, model.attributes.type);
    // for templated type changes we delete all tokens (new ones will be added during the templating stage), otherwise reset to initial values
    const resetTokens = isChangingTemplate ? [] : model.attributes?.tokens || [model.initialTokenValue];
    // change of template is a special case of editing which is closer to a new credential
    this.setState({ isChangingTemplate });
    // when we change the type, need to reset the tokens
    const tokensField = form.getField('tokens');
    tokensField.reset(resetTokens);
    form.setValue('type', type);
    this.applyTemplate(type);
  };

  applyTemplate = (type) => {
    const { form, props } = this;
    const { $auth } = props;
    const template = CREDENTIAL_TYPE_TEMPLATES[type];

    const tokensField = form.getField('tokens');

    if (template) {
      this.setState({ isCredentialFromTemplate: true });
      tokensField.setValue([]);

      template.forEach(({ key, options, rules, optional, permissionFlag }, index) => {
        // Skip this field if it has a permission flag and the user doesn't have permission
        if (permissionFlag && !$auth.hasPermission(permissionFlag, { overrideForSudo: false })) {
          return;
        }

        tokensField.add({ key, value: '' });

        if (options) {
          tokensField.at(index).value.setOptions(options);
        }

        if (optional) {
          tokensField.at(index).value.optional = true;
          tokensField.at(index).value.placeholder = 'optional';
        }
        if (rules) {
          tokensField.at(index).value.setRules(rules);
        }
      });
    } else {
      this.setState({ isCredentialFromTemplate: false });
    }
  };

  render() {
    const { model, onClose, isOpen = true } = this.props;
    const { isCredentialFromTemplate, isChangingTemplate, confirmDialogOpen, isLoading, storedValues } = this.state;

    let nameRules = fields.name.rules;

    // change of template is a special case of editing which is closer to a new credential
    const isEditing = !model.isNew && !isChangingTemplate;

    if (model.isNew) {
      nameRules += `|not_in:${model.collection.map((cred) => cred.get('name'))}`;
    }

    const typeField = (
      <Field name="type" large onChange={this.handleTypeChange} m={0} disabled={this.isConsumedBySynthetics}>
        <Select menuWidth={350} optionRenderer={CredentialTypeOptionRenderer} />
      </Field>
    );

    return (
      <FormDialog
        entityName="Credential"
        fields={fields}
        options={{ name: 'Credentials Dialog Form', showPristineErrors: false }}
        model={model}
        isOpen={isOpen}
        onClose={onClose}
        width={700}
        formComponentProps={{
          onLoad: (form) => (this.form = form)
        }}
        formActionsProps={{
          onSubmit: this.handleSave,
          onCancel: onClose,
          forceLoading: isLoading
        }}
      >
        <Confirm
          onConfirm={() => this.finalizeSave(this.form, storedValues)}
          onCancel={() => this.setState({ confirmDialogOpen: false })}
          isOpen={confirmDialogOpen}
          deviceCount={model.deviceConsumers.length}
        />
        <Field name="name" autoFocus fill large rules={nameRules} disabled={!model.isNew}>
          <InputGroup />
        </Field>
        <Field name="description" fill large>
          <InputGroup />
        </Field>
        <Flex gap={1} alignItems="center" mb={2}>
          {this.isConsumedBySynthetics ? (
            <Tooltip content="Changing type is currently unsupported for credentials which are used by synthetic tests.">
              {typeField}
            </Tooltip>
          ) : (
            typeField
          )}
          <Box flex={1}>
            <LabelSelector fieldName="labels" fill m={0} />
          </Box>
        </Flex>

        <Card p={2}>
          <Field name="tokens">
            {({ field: tokensField }) => (
              <>
                <Flex flexDirection="column" gap="12px">
                  <InputMultiple field={tokensField} addButtonText="Add">
                    {({ field }) =>
                      field.map((f, index) => {
                        const isNewField = !(index < model.get('credentials')?.length);
                        const preExisting = isEditing && !isNewField;
                        const rules = f.value?.rules || [];

                        if (!preExisting && !f.value.optional && !f.value.options?.find((opt) => opt.value === '')) {
                          rules.push('required');
                        }

                        return (
                          <>
                            <Flex alignItems="center" key={f.key._id} gap="12px">
                              <Field
                                field={f.key}
                                disabled={isCredentialFromTemplate || !isNewField}
                                showError={false}
                                showLabel={index === 0}
                                large
                                width={200}
                                m={0}
                              >
                                <InputGroup />
                              </Field>
                              <Field
                                field={f.value}
                                rules={rules}
                                large
                                flex={1}
                                m={0}
                                showError={false}
                                showLabel={index === 0}
                                fill
                                placeholder={preExisting ? '************' : undefined}
                              >
                                {f.value?.options?.length ? (
                                  <Select fill />
                                ) : (
                                  <PasswordInput
                                    showVisibleToggle={!preExisting || f.value.dirty}
                                    autoComplete="new-password"
                                  />
                                )}
                              </Field>

                              {!isCredentialFromTemplate && isNewField && (
                                <Button
                                  intent={index > 0 ? 'danger' : 'none'}
                                  minimal
                                  css={{ alignSelf: 'center' }}
                                  onClick={() => this.handleRemoveToken(field, index)}
                                  icon={index > 0 ? 'trash' : 'blank'}
                                  disabled={index === 0}
                                />
                              )}
                            </Flex>
                            <SingleLineErrors fields={[f.key, f.value]} inline />
                          </>
                        );
                      })
                    }
                  </InputMultiple>
                </Flex>
                {!isCredentialFromTemplate && (
                  <Flex alignItems="center" mt="12px">
                    <Button icon="plus" onClick={() => this.handleAddToken(tokensField)} text="Add" />
                  </Flex>
                )}
              </>
            )}
          </Field>
        </Card>
      </FormDialog>
    );
  }
}
